import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { BarChart3, Loader2, RefreshCw, Cpu } from 'lucide-react';

const FEATURE_META = {
  setup_assistant: { label: 'Setup Assistant', color: 'bg-green-500' },
  marketing_content: { label: 'Marketing', color: 'bg-cyan-500' },
  business_advice: { label: 'Business Coach', color: 'bg-yellow-500' },
  documentation: { label: 'Doc Generator', color: 'bg-fuchsia-500' },
};

const fmt = (n) => (n || 0).toLocaleString('en-US');

export const UsagePanel = ({ usage, usageLoading, refreshUsage, isSetup }) => {
  const breakdown = usage?.feature_breakdown || {};
  const total = usage?.total_tokens || 0;
  const rows = Object.keys(FEATURE_META)
    .map((key) => ({ key, ...FEATURE_META[key], tokens: breakdown[key] || 0 }))
    .sort((a, b) => b.tokens - a.tokens);
  const maxTokens = Math.max(1, ...rows.map((r) => r.tokens));

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="usage-panel">
      <Card className="card-tech p-8">
        <div className="flex items-start justify-between gap-4 mb-2">
          <h2 className="text-2xl font-bold text-green-400">
            <BarChart3 className="w-6 h-6 inline mr-2" /> Token Usage
          </h2>
          <Button onClick={refreshUsage} disabled={usageLoading} className="btn-secondary px-3 py-2 shrink-0"
            data-testid="usage-refresh-btn" aria-label="Refresh usage">
            {usageLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </div>
        <p className="text-green-300 mb-6">
          OpenAI tokens consumed by each Pro tool using your key. Watch your spend in real time.
        </p>

        {!isSetup ? (
          <div className="p-4 bg-green-500/10 border border-green-500/30 rounded text-green-300 text-sm"
            data-testid="usage-no-key">
            Save your OpenAI API key in Settings to start tracking token usage.
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-4 mb-8">
              <div className="p-4 bg-black/50 border border-green-500/30 rounded" data-testid="usage-total-tokens">
                <p className="text-green-500/70 text-xs uppercase tracking-wide">Total Tokens</p>
                <p className="text-3xl font-bold text-green-400 mt-1">{fmt(total)}</p>
              </div>
              <div className="p-4 bg-black/50 border border-green-500/30 rounded" data-testid="usage-total-requests">
                <p className="text-green-500/70 text-xs uppercase tracking-wide">AI Requests</p>
                <p className="text-3xl font-bold text-green-400 mt-1">{fmt(usage?.total_requests)}</p>
              </div>
            </div>

            <div className="space-y-4">
              {rows.map((r) => (
                <div key={r.key} data-testid={`usage-row-${r.key}`}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-green-300 font-semibold">{r.label}</span>
                    <span className="text-green-400 font-mono">{fmt(r.tokens)} tokens</span>
                  </div>
                  <div className="h-2.5 w-full bg-black/60 border border-green-500/20 rounded-full overflow-hidden">
                    <div className={`h-full ${r.color} transition-[width] duration-500`}
                      style={{ width: `${(r.tokens / maxTokens) * 100}%` }} />
                  </div>
                </div>
              ))}
            </div>

            {total === 0 && (
              <p className="text-green-500/60 text-sm mt-6 text-center" data-testid="usage-empty">
                No AI usage yet. Your token counts appear here after you use the Pro tools.
              </p>
            )}
          </>
        )}
      </Card>

      {isSetup && usage?.recent_usage?.length > 0 && (
        <Card className="card-tech p-6" data-testid="usage-recent">
          <h3 className="text-lg font-bold text-green-400 mb-4">
            <Cpu className="w-5 h-5 inline mr-2" /> Recent Requests
          </h3>
          <div className="space-y-2">
            {[...usage.recent_usage].reverse().map((r, i) => (
              <div key={`${r.created_at || i}-${r.feature}-${r.tokens_used}`} className="flex justify-between items-center text-sm border-b border-green-500/10 pb-2"
                data-testid={`usage-recent-row-${i}`}>
                <span className="text-green-300">{FEATURE_META[r.feature]?.label || r.feature}</span>
                <span className="text-green-400 font-mono">{fmt(r.tokens_used)} tokens</span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};
